import React, { useContext, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import moment from 'moment-timezone';
import { AppConstants } from '../../../../shared/Constants';
import { getTypeDisplayName } from '../helpers/documents.helper';
import { Button } from 'primereact/button';
import { AccountContext } from '../../../../components/AccountContext';
import { NotificationContext } from '../../../../components/GrowlNotifications';
import { useMutation } from '@apollo/react-hooks';
import { UPDATE_DOCUMENT_STATUS_MUTATION } from '../services/document-management.service';
import { growlActionSuccess, growlErrors } from '../../../../utils/NotificationUtil';
import UpdateDocumentScheduledOnDialog from './UpdateDocumentScheduledOnDialog';
import UpdateDocumentContentDialog from './UpdateDocumentContentDialog';

const DocumentsList = ({ loading, documents }) => {
  const [selectedDocument, setSelectedDocument] = useState({});
  const [showUpdateDocumentScheduledOnDialog, setShowUpdateDocumentScheduledOnDialog] = useState(false);
  const [showUpdateDocumentContentDialog, setShowUpdateDocumentContentDialog] = useState(false);

  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);

  const [updateDocumentStatus] = useMutation(UPDATE_DOCUMENT_STATUS_MUTATION);

  const updateStatus = async (documentId, status) => {
    try {
      await updateDocumentStatus({
        variables: {
          input: {
            accountId: currentAccount,
            documentId,
            status,
          },
        },
        refetchQueries: ['documentListQuery'],
      });

      growlActionSuccess(growl);
    } catch (mutationError) {
      growlErrors(growl, mutationError);
    }
  };

  const typeColumnBody = (rowData) => {
    const { type } = rowData;

    return getTypeDisplayName(type);
  };

  const updatedByColumnBody = (rowData) => {
    const {
      updatedBy: { firstName, lastName },
    } = rowData;

    return `${firstName} ${lastName}`;
  };

  const scheduledOnColumnBody = (rowData) => {
    const { scheduledOn } = rowData;

    return <div>{moment(scheduledOn).format(AppConstants.GSC_DASHBOARD_DATETIME_INPUT_FORMAT)}</div>;
  };

  const enforcedOnColumnBody = (rowData) => {
    const { enforcedOn } = rowData;

    if (!!enforcedOn) {
      return <div>{moment(enforcedOn).format(AppConstants.GSC_DASHBOARD_DATETIME_INPUT_FORMAT)}</div>;
    }

    return '-';
  };

  const contentColumnBody = (rowData) => {
    const { url } = rowData;

    return <a href={url}>Click to see document</a>;
  };

  const actionsColumnBody = (document) => {
    const { id, status } = document;

    return (
      <div>
        <Button
          type="button"
          icon={status === 'DRAFT' ? 'pi pi-lock' : 'pi pi-lock-open'}
          className="p-button-secondary"
          tooltip="Update status"
          tooltipOptions={{
            position: 'top',
          }}
          onClick={() => {
            const documentStatus = status === 'DRAFT' ? 'LOCKED' : 'DRAFT';

            updateStatus(id, documentStatus);
          }}
          disabled={status !== 'DRAFT' && status !== 'LOCKED'}
        />
        <Button
          type="button"
          icon="pi pi-calendar"
          className="p-button-secondary"
          tooltip="Update scheduled on date"
          tooltipOptions={{
            position: 'top',
          }}
          onClick={() => {
            setSelectedDocument(document);
            setShowUpdateDocumentScheduledOnDialog(true);
          }}
          disabled={status !== 'DRAFT'}
        />
        <Button
          type="button"
          icon="pi pi-file"
          className="p-button-secondary"
          tooltip="Update document content"
          tooltipOptions={{
            position: 'top',
          }}
          onClick={() => {
            setSelectedDocument(document);
            setShowUpdateDocumentContentDialog(true);
          }}
          disabled={status !== 'DRAFT'}
        />
      </div>
    );
  };

  return (
    <>
      <UpdateDocumentScheduledOnDialog
        documentId={selectedDocument.id}
        documentType={selectedDocument.type}
        showUpdateDocumentScheduledOnDialog={showUpdateDocumentScheduledOnDialog}
        hideUpdateDocumentScheduledOnDialog={() => setShowUpdateDocumentScheduledOnDialog(false)}
      />
      <UpdateDocumentContentDialog
        documentId={selectedDocument.id}
        documentType={selectedDocument.type}
        documentVersion={selectedDocument.versionNumber}
        showUpdateDocumentContentDialog={showUpdateDocumentContentDialog}
        hideUpdateDocumentContentDialog={() => setShowUpdateDocumentContentDialog(false)}
      />
      <DataTable value={documents} loading={loading} emptyMessage="There are currently no legal documents to manage">
        <Column field="id" header="ID" style={{ width: '60px' }} bodyClassName="center" />
        <Column field="type" header="Type" body={typeColumnBody} bodyClassName="center" sortable />
        <Column
          field="versionNumber"
          header="Version number"
          style={{ width: '160px' }}
          bodyClassName="center"
          sortable
        />
        <Column field="updatedBy" header="Updated by" body={updatedByColumnBody} bodyClassName="center" />
        <Column field="scheduledOn" header="Scheduled on" body={scheduledOnColumnBody} bodyClassName="center" />
        <Column field="enforcedOn" header="Enforced on" body={enforcedOnColumnBody} bodyClassName="center" />
        <Column field="status" header="Status" bodyClassName="center" />
        <Column field="url" header="Content" body={contentColumnBody} bodyClassName="center" />
        <Column header="Actions" body={actionsColumnBody} bodyClassName="center" />
      </DataTable>
    </>
  );
};

export default DocumentsList;
